<template>
  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/tariff-plus" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Tariff Plus Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>View</span>
        </span>
        <span class="sba-collection-context">Tariff Management Workspace</span>
      </div>
    </div>
  </nav>

  <div id="top"></div>

  <div class="sba-collection-page-surface is-workbench tariff-plus-secondary-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div class="tariff-plus-workbench sba-slide-in" v-else>
        <section class="tariff-plus-secondary-header">
          <section class="tariff-plus-super-card">
            <button type="button" class="secondary-page-back" title="Back to Tariff Plus"
              @click.prevent="handleActive(home)">
              <i class="material-icons">keyboard_return</i>
            </button>
            <div class="tariff-plus-super-copy">
              <button type="button" class="secondary-parent-link" @click.prevent="handleActive(home)">
                All Tariff Plus
              </button>
              <small>Back to provider tariff list</small>
            </div>
          </section>

          <section class="tariff-plus-dossier-card">
            <div class="tariff-plus-avatar" aria-hidden="true">
              {{ providerInitials }}
            </div>
            <div class="tariff-plus-dossier-title">
              <label class="active teal-text">Tariff Plus</label>
              <h5>{{ capWords(provider?.name || "Provider") }}</h5>
              <p>Review prices, add single tariff items, or replace this provider's tariff records.</p>
            </div>

            <div class="tariff-plus-dossier-context">
              <span class="context-pill" :class="providerStatusClass">{{ providerStatusLabel }}</span>
              <span class="context-pill">{{ provider?.state || "State not recorded" }}</span>
              <span class="context-pill">{{ provider?.phone || "No phone recorded" }}</span>
            </div>
          </section>

          <section class="tariff-plus-mode-strip">
            <div class="tariff-plus-mode-copy">
              <span>{{ currentMode.title }}</span>
              <small>{{ currentMode.description }}</small>
            </div>
            <div class="tariff-plus-mode-actions">
              <button
                v-for="mode in tariffModes"
                :key="mode.key"
                type="button"
                class="tariff-plus-mode-option"
                :class="{ 'is-active': currentMode.key === mode.key }"
                @click.prevent="handleMode(mode)"
              >
                <i class="material-icons">{{ mode.icon }}</i>
                <span>{{ mode.title }}</span>
              </button>
            </div>
          </section>
        </section>

        <section class="tariff-plus-secondary-body-frame">
          <TariffDiv
            :key="`${currentMode.key}-${provider.id}-${provider.schedule_id}-${refreshKey}`"
            :provider="provider"
            :startTab="currentMode.key"
            @reload="handleReload"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItem from "@/composables/getItem";
import { capWordsFx } from "@/composables/fxLibrary/coreFx.js";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import TariffDiv from "@/components/provider/TariffProviderComponent.vue";

const tariffModes = [
  {
    key: "view",
    title: "View All",
    description: "Review provider tariff items and current prices.",
    icon: "visibility",
  },
  {
    key: "add",
    title: "Add Item",
    description: "Add a new tariff item for this provider.",
    icon: "add_circle",
  },
  {
    key: "replace",
    title: "Replace All",
    description: "Replace this provider's tariff records in bulk.",
    icon: "upload",
  },
];

export default {
  name: "tariffPlusView",
  components: { SubHeading, LoadingDiv, TariffDiv },
  props: ["id"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const currentMode = ref(tariffModes[0]);
    const refreshKey = ref(0);

    const { routeToTab } = useModuleNavigation();

    onMounted(() => {
      document.title = "Tariff Plus";
    });

    onMounted(() => {
      tabs.value.push({ id: 1, name: "Tariff Plus", title: "All", icon: "arrow_back", url: "tariffPlus", hash: null });
      tabs.value.push({ id: 2, name: "Tariff Plus", title: "View", icon: null, url: "tariffPlus", hash: "#view" });

      currTab.value = tabs.value.find(x => x.title.includes("View"));
      home.value = tabs.value.find(x => x.id === 1);
    });

    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    const handleMode = (mode) => {
      currentMode.value = mode;
    };

    const capWords = (val) => {
      return capWordsFx(val);
    };

    const getProvider = async () => {
      const { res, err } = await getItem("provider", props.id);

      if (res.value) {
        response.value = res.value.data;
      } else {
        error.value = err.value;
      }

      isReady.value = true;
    };

    onMounted(() => {
      getProvider();
    });

    const handleReload = async (val) => {
      if (val) {
        await getProvider();
        refreshKey.value++;
      }
    };

    const provider = computed(() => {
      if (!response.value) {
        return {};
      } else {
        return response.value;
      }
    });

    const providerInitials = computed(() => {
      const name = provider.value?.name || "Provider";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x.charAt(0))
        .join("")
        .toUpperCase();
    });

    const providerStatusLabel = computed(() => {
      if (provider.value?.status === "s") {
        return "Inactive provider";
      }

      if (provider.value?.status === "p") {
        return "Pending approval";
      }

      return "Active provider";
    });

    const providerStatusClass = computed(() => {
      if (provider.value?.status === "s") {
        return "is-inactive";
      }

      if (provider.value?.status === "p") {
        return "is-pending";
      }

      return "is-active";
    });

    return {
      handleActive,
      handleMode,
      handleReload,
      home,
      currTab,
      tabs,
      capWords,
      error,
      isReady,
      provider,
      providerInitials,
      providerStatusClass,
      providerStatusLabel,
      currentMode,
      refreshKey,
      tariffModes,
    };
  },
};
</script>

<style scoped>
.tariff-plus-secondary-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
}

.tariff-plus-workbench {
  color: #263238;
  padding-bottom: 48px;
  padding-top: 0;
  position: relative;
  width: 100%;
  z-index: 1;
}

.tariff-plus-super-card,
.tariff-plus-dossier-card,
.tariff-plus-mode-strip,
.tariff-plus-secondary-body-frame,
.tariff-plus-workbench :deep(.provider-tariff-card),
.tariff-plus-workbench :deep(.provider-tariff-search-card),
.tariff-plus-workbench :deep(.provider-tariff-row),
.tariff-plus-workbench :deep(.provider-tariff-empty) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.tariff-plus-secondary-header {
  box-shadow: 0 20px 34px -32px rgba(38, 50, 56, 0.82);
  margin-bottom: 0;
  overflow: visible;
  position: relative;
}

.tariff-plus-super-card {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  display: flex;
  gap: 10px;
  min-height: 46px;
  padding: 10px 14px;
  position: relative;
  width: 100%;
  z-index: 3;
}

.tariff-plus-super-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.15;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 24px;
}

.secondary-page-back:hover {
  background: #00695c;
  border-color: #00695c;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  margin: 0;
  padding: 0;
  text-align: left;
  text-transform: uppercase;
  transition: color 0.18s ease;
}

.secondary-parent-link:hover {
  color: #00897b;
}

.tariff-plus-super-copy small {
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
}

.tariff-plus-dossier-card {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: grid;
  gap: 22px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 26px 34px;
  position: relative;
}

.tariff-plus-avatar {
  align-items: center;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 50%;
  color: #00695c;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 1.25rem;
  font-weight: 800;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.tariff-plus-dossier-title {
  min-width: 0;
}

.tariff-plus-dossier-title label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.tariff-plus-dossier-title h5 {
  color: #263238;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 8px;
  overflow-wrap: anywhere;
}

.tariff-plus-dossier-title p {
  color: #607d8b;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
  max-width: 760px;
}

.tariff-plus-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 430px;
}

.context-pill {
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.context-pill.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.context-pill.is-inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.context-pill.is-pending {
  background: #fff8e1;
  border-color: #ffe082;
  color: #795548;
}

.tariff-plus-mode-strip {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 14px 18px;
}

.tariff-plus-mode-copy span {
  color: #263238;
  display: block;
  font-weight: 800;
}

.tariff-plus-mode-copy small {
  color: #607d8b;
  display: block;
  margin-top: 3px;
}

.tariff-plus-mode-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.tariff-plus-mode-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 5px;
  color: #455a64;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  gap: 8px;
  min-height: 38px;
  padding: 0 13px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.tariff-plus-mode-option:hover,
.tariff-plus-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  color: #ffffff;
}

.tariff-plus-mode-option i {
  color: inherit;
  font-size: 18px;
}

.tariff-plus-secondary-body-frame {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  box-sizing: border-box;
  margin-top: 14px;
  padding: 16px;
  position: relative;
}

.tariff-plus-secondary-body-frame :deep(.provider-tariff-card),
.tariff-plus-secondary-body-frame :deep(.provider-tariff-row),
.tariff-plus-secondary-body-frame :deep(.provider-tariff-empty),
.tariff-plus-secondary-body-frame :deep(.tariff-single-form),
.tariff-plus-secondary-body-frame :deep(.tariff-upload-form) {
  border-color: #c4d3d9 !important;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.tariff-plus-secondary-body-frame :deep(.provider-tariff-row:hover) {
  border-color: #80cbc4 !important;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62) !important;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 992px) {
  .tariff-plus-dossier-card {
    align-items: center;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .tariff-plus-dossier-context {
    align-items: flex-start;
    grid-column: 1 / -1;
    justify-content: flex-start;
    max-width: none;
    width: 100%;
  }

  .tariff-plus-mode-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .tariff-plus-mode-actions {
    justify-content: flex-start;
  }
}

@media only screen and (max-width: 600px) {
  .tariff-plus-dossier-card,
  .tariff-plus-mode-strip,
  .tariff-plus-secondary-body-frame {
    padding: 20px;
  }

  .tariff-plus-dossier-card {
    gap: 14px;
  }

  .tariff-plus-avatar {
    font-size: 1.05rem;
    height: 48px;
    width: 48px;
  }

  .tariff-plus-dossier-title h5 {
    font-size: 1.35rem;
    margin-bottom: 6px;
  }

  .tariff-plus-dossier-title p {
    font-size: 0.94rem;
  }

  .tariff-plus-mode-option {
    justify-content: center;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tariff-plus-super-card,
  .tariff-plus-dossier-card,
  .tariff-plus-mode-strip,
  .tariff-plus-secondary-body-frame,
  .tariff-plus-workbench :deep(.provider-tariff-card),
  .tariff-plus-workbench :deep(.provider-tariff-search-card),
  .tariff-plus-workbench :deep(.provider-tariff-row),
  .tariff-plus-workbench :deep(.provider-tariff-empty) {
    animation: none;
  }
}
</style>
